<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { upsertNewsletterAudience } from '@/api/newsletters'
import AppSpinner from '@/components/shared/AppSpinner.vue'
import { useRecipientCount } from '@/composables/useRecipientCount'
import type {
  NewsletterAudience,
  NewsletterSeedType,
  UpsertAudiencePayload,
  ManualAddition,
} from '@/types/newsletter'
import { NewsletterSeedType as SeedType } from '@/types/newsletter'
import type { Event, Edition, Race } from '@/types/events'
import { listEvents, listEditions, listRaces } from '@/api/events'

const { t } = useI18n()

const props = defineProps<{
  newsletterId: number
  organisationId: number
  modelValue: NewsletterAudience | null
}>()

const emit = defineEmits<{
  'update:modelValue': [audience: NewsletterAudience]
  close: []
  saved: []
}>()

// Registration status options (resolved reactively so they update on locale change)
const REGISTRATION_STATUSES = computed(() => [
  { value: 'completed',    label: t('newsletters.audience.status_confirmed') },
  { value: 'waiting_list', label: t('newsletters.audience.status_waitlist') },
  { value: 'cancelled',    label: t('newsletters.audience.status_cancelled') },
  { value: 'payment_expired', label: t('newsletters.audience.status_payment_expired') },
])

// Local form state
const seedType    = ref<NewsletterSeedType>(props.modelValue?.seed_type ?? SeedType.Event)
const seedId      = ref<number | null>(props.modelValue?.seed_id ?? null)
const statuses    = ref<string[]>(props.modelValue?.registration_statuses ?? ['completed'])
const manualAdditions   = ref<ManualAddition[]>(
  (props.modelValue?.manual_additions ?? []).map((a) => ({ ...a })),
)
const manualExclusions  = ref<number[]>([...(props.modelValue?.manual_exclusions ?? [])])

// Cascading seed dropdowns
const events   = ref<Event[]>([])
const editions = ref<Edition[]>([])
const races    = ref<Race[]>([])
const selectedEventId   = ref<number | null>(null)
const selectedEditionId = ref<number | null>(null)
const loadingEvents   = ref(false)
const loadingEditions = ref(false)
const loadingRaces    = ref(false)

// Saving state
const saving = ref(false)
const saveError = ref<string | null>(null)

// Manual email addition input
const newEmail = ref('')
const newName  = ref('')
const emailError = ref('')

// Recipient count (debounced)
const { count, loading: countLoading, refresh: refreshCount } = useRecipientCount(props.organisationId)

async function loadEvents(): Promise<void> {
  loadingEvents.value = true
  try {
    events.value = await listEvents(props.organisationId)
  } finally {
    loadingEvents.value = false
  }
}

async function loadEditions(eventId: number): Promise<void> {
  const eventSlug = events.value.find((e) => e.id === eventId)?.slug
  if (!eventSlug) return
  loadingEditions.value = true
  editions.value = []
  races.value = []
  try {
    editions.value = await listEditions(eventSlug)
  } finally {
    loadingEditions.value = false
  }
}

async function loadRaces(eventId: number, editionId: number): Promise<void> {
  const eventSlug = events.value.find((e) => e.id === eventId)?.slug
  const editionSlug = editions.value.find((ed) => ed.id === editionId)?.slug
  if (!eventSlug || !editionSlug) return
  loadingRaces.value = true
  races.value = []
  try {
    races.value = await listRaces(eventSlug, editionSlug)
  } finally {
    loadingRaces.value = false
  }
}

function onEventChange(eventId: number): void {
  selectedEventId.value = eventId
  selectedEditionId.value = null
  seedId.value = null
  editions.value = []
  races.value = []
  if (seedType.value === SeedType.Event) {
    seedId.value = eventId
  }
  void loadEditions(eventId)
  void triggerCountRefresh()
}

function onEditionChange(editionId: number): void {
  selectedEditionId.value = editionId
  seedId.value = null
  races.value = []
  if (seedType.value === SeedType.Edition) {
    seedId.value = editionId
  }
  if (selectedEventId.value) {
    void loadRaces(selectedEventId.value, editionId)
  }
  void triggerCountRefresh()
}

function onRaceChange(raceId: number): void {
  if (seedType.value === SeedType.Race) {
    seedId.value = raceId
  }
  void triggerCountRefresh()
}

function onSeedTypeChange(): void {
  seedId.value = null
  if (seedType.value === SeedType.Event && selectedEventId.value) {
    seedId.value = selectedEventId.value
  } else if (seedType.value === SeedType.Edition && selectedEditionId.value) {
    seedId.value = selectedEditionId.value
  }
  void triggerCountRefresh()
}

function onStatusChange(): void {
  void triggerCountRefresh()
}

function triggerCountRefresh(): void {
  if (seedId.value !== null) {
    void refreshCount(props.newsletterId, {
      seed_type: seedType.value,
      seed_id: seedId.value,
      registration_statuses: statuses.value,
      manual_additions: manualAdditions.value,
      manual_exclusions: manualExclusions.value,
    })
  }
}

// Email validation
function isValidEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
}

function addManualEmail(): void {
  const trimmed = newEmail.value.trim().toLowerCase()
  if (!trimmed) return
  if (!isValidEmail(trimmed)) {
    emailError.value = t('newsletters.audience.email_invalid')
    return
  }
  if (manualAdditions.value.some((a) => a.email === trimmed)) {
    emailError.value = t('newsletters.audience.email_duplicate')
    return
  }
  manualAdditions.value.push({ email: trimmed, name: newName.value.trim() || null })
  newEmail.value = ''
  newName.value = ''
  emailError.value = ''
  void triggerCountRefresh()
}

function removeManualAddition(index: number): void {
  manualAdditions.value.splice(index, 1)
  void triggerCountRefresh()
}

// Computed audience payload
const payload = computed<UpsertAudiencePayload | null>(() => {
  if (seedId.value === null) return null
  return {
    seed_type:              seedType.value,
    seed_id:                seedId.value,
    registration_statuses:  statuses.value,
    manual_additions:       manualAdditions.value,
    manual_exclusions:      manualExclusions.value,
  }
})

async function handleSave(): Promise<void> {
  if (!payload.value) return
  saving.value = true
  saveError.value = null
  try {
    const audience = await upsertNewsletterAudience(
      props.organisationId,
      props.newsletterId,
      payload.value,
    )
    emit('update:modelValue', audience)
    emit('saved')
    emit('close')
  } catch {
    saveError.value = t('newsletters.audience.save_error')
  } finally {
    saving.value = false
  }
}

// Kick off loading
onMounted(async () => {
  await loadEvents()
  // Pre-populate from existing audience
  if (props.modelValue) {
    if (props.modelValue.seed_type === SeedType.Event) {
      selectedEventId.value = props.modelValue.seed_id
      await loadEditions(props.modelValue.seed_id)
    } else if (props.modelValue.seed_type === SeedType.Edition) {
      // Need to find which event this edition belongs to
      // We'll select the first event for now and load editions
      if (events.value.length > 0 && events.value[0]) {
        selectedEventId.value = events.value[0].id
        await loadEditions(events.value[0].id)
        const found = editions.value.find((e) => e.id === props.modelValue!.seed_id)
        if (found) {
          selectedEditionId.value = found.id
        }
      }
    } else if (props.modelValue.seed_type === SeedType.Race) {
      if (events.value.length > 0 && events.value[0]) {
        selectedEventId.value = events.value[0].id
        await loadEditions(events.value[0].id)
      }
    }
    triggerCountRefresh()
  }
})

// Watch statuses to refresh count
watch(statuses, () => void triggerCountRefresh(), { deep: true })
</script>

<template>
  <!-- Full-screen focus-trapped overlay -->
  <div
    class="fixed inset-0 z-50 flex flex-col bg-slate-900"
    role="dialog"
    aria-modal="true"
    :aria-label="t('newsletters.audience.overlay_aria_label')"
  >
    <!-- Header -->
    <div class="flex items-center justify-between px-6 py-4 border-b border-slate-700 shrink-0">
      <h2 class="text-lg font-bold text-white">{{ t('newsletters.audience.header_title') }}</h2>
      <button
        type="button"
        class="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:text-white hover:bg-slate-700/50 transition-colors"
        :aria-label="t('newsletters.audience.close_button_aria')"
        @click="emit('close')"
      >
        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>

    <!-- Sticky live count bar -->
    <div
      class="sticky top-0 z-10 px-6 py-3 bg-slate-800/80 backdrop-blur border-b border-slate-700 shrink-0"
      aria-live="polite"
      aria-atomic="true"
    >
      <div class="flex items-center gap-4 text-sm">
        <span v-if="countLoading" class="text-slate-400">{{ t('newsletters.audience.live_count_calculating') }}</span>
        <template v-else-if="count">
          <span class="text-white font-semibold">{{ t('newsletters.audience.live_count_will_receive', { count: count.total.toLocaleString() }) }}</span>
          <span class="text-slate-400">&middot; {{ t('newsletters.audience.live_count_unsubscribed', { count: count.suppressed.toLocaleString() }) }}</span>
          <span v-if="count.manual_additions > 0" class="text-slate-400">&middot; {{ t('newsletters.audience.live_count_manual_additions', { count: count.manual_additions.toLocaleString() }) }}</span>
          <span v-if="count.excluded > 0" class="text-slate-400">&middot; {{ t('newsletters.audience.live_count_excluded', { count: count.excluded.toLocaleString() }) }}</span>
        </template>
        <span v-else class="text-slate-500">{{ t('newsletters.audience.live_count_select_seed') }}</span>
      </div>
    </div>

    <!-- Scrollable body -->
    <div class="flex-1 overflow-y-auto">
      <div class="max-w-3xl mx-auto px-6 py-6 space-y-8">

        <!-- Section 1: Audience seed -->
        <section>
          <h3 class="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-4">
            {{ t('newsletters.audience.section_seed_title') }}
          </h3>

          <!-- Seed type selector -->
          <div class="flex gap-2 mb-4">
            <button
              v-for="st in [{ value: SeedType.Event, label: t('newsletters.audience.seed_type_event') }, { value: SeedType.Edition, label: t('newsletters.audience.seed_type_edition') }, { value: SeedType.Race, label: t('newsletters.audience.seed_type_race') }]"
              :key="st.value"
              type="button"
              class="px-3 py-1.5 text-sm rounded-lg border transition-colors"
              :class="seedType === st.value
                ? 'border-emerald-500 bg-emerald-500/15 text-emerald-400'
                : 'border-slate-600 text-slate-400 hover:border-slate-500 hover:text-white'"
              @click="seedType = st.value; onSeedTypeChange()"
            >
              {{ st.label }}
            </button>
          </div>

          <!-- Cascading dropdowns -->
          <div class="flex flex-wrap gap-3">
            <!-- Event -->
            <div class="min-w-48">
              <label class="block text-xs text-slate-400 mb-1">{{ t('newsletters.audience.event_label') }}</label>
              <select
                class="w-full bg-slate-800 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white focus:border-emerald-500 focus:outline-none"
                :value="selectedEventId ?? ''"
                @change="onEventChange(Number(($event.target as HTMLSelectElement).value))"
              >
                <option value="" disabled>{{ t('newsletters.audience.event_placeholder') }}</option>
                <option
                  v-for="ev in events"
                  :key="ev.id"
                  :value="ev.id"
                >{{ ev.name }}</option>
              </select>
            </div>

            <!-- Edition (when seed type is Edition or Race) -->
            <div v-if="seedType !== SeedType.Event" class="min-w-48">
              <label class="block text-xs text-slate-400 mb-1">{{ t('newsletters.audience.edition_label') }}</label>
              <select
                :disabled="!selectedEventId || loadingEditions"
                class="w-full bg-slate-800 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white focus:border-emerald-500 focus:outline-none disabled:opacity-50"
                :value="selectedEditionId ?? ''"
                @change="onEditionChange(Number(($event.target as HTMLSelectElement).value))"
              >
                <option value="" disabled>{{ t('newsletters.audience.edition_placeholder') }}</option>
                <option
                  v-for="ed in editions"
                  :key="ed.id"
                  :value="ed.id"
                >{{ ed.name }}</option>
              </select>
            </div>

            <!-- Race (when seed type is Race) -->
            <div v-if="seedType === SeedType.Race" class="min-w-48">
              <label class="block text-xs text-slate-400 mb-1">{{ t('newsletters.audience.race_label') }}</label>
              <select
                :disabled="!selectedEditionId || loadingRaces"
                class="w-full bg-slate-800 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white focus:border-emerald-500 focus:outline-none disabled:opacity-50"
                :value="seedId ?? ''"
                @change="onRaceChange(Number(($event.target as HTMLSelectElement).value))"
              >
                <option value="" disabled>{{ t('newsletters.audience.race_placeholder') }}</option>
                <option
                  v-for="race in races"
                  :key="race.id"
                  :value="race.id"
                >{{ race.name }}</option>
              </select>
            </div>
          </div>
        </section>

        <!-- Section 2: Status filter -->
        <section>
          <h3 class="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-4">
            {{ t('newsletters.audience.section_status_title') }}
          </h3>
          <div class="flex flex-wrap gap-4">
            <label
              v-for="s in REGISTRATION_STATUSES"
              :key="s.value"
              class="flex items-center gap-2 cursor-pointer"
            >
              <input
                type="checkbox"
                :value="s.value"
                v-model="statuses"
                class="rounded border-slate-600 bg-slate-800 text-emerald-500 focus:ring-emerald-500"
                @change="onStatusChange"
              />
              <span class="text-sm text-slate-300">{{ s.label }}</span>
            </label>
          </div>
          <p v-if="statuses.length === 0" class="mt-2 text-xs text-red-400">
            {{ t('newsletters.audience.status_required') }}
          </p>
        </section>

        <!-- Section 3: Adjust list (manual additions) -->
        <section>
          <h3 class="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-4">
            {{ t('newsletters.audience.section_adjust_title') }}
          </h3>

          <!-- Add external email -->
          <div class="mb-4">
            <p class="text-xs text-slate-400 mb-2">{{ t('newsletters.audience.add_external_email_label') }}</p>
            <div class="flex gap-2 flex-wrap">
              <div class="flex-1 min-w-48">
                <input
                  v-model="newEmail"
                  type="email"
                  :placeholder="t('newsletters.audience.email_placeholder')"
                  class="w-full bg-slate-800 border rounded-lg px-3 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-emerald-500"
                  :class="emailError ? 'border-red-500' : 'border-slate-600'"
                  @keydown.enter.prevent="addManualEmail"
                />
                <p v-if="emailError" class="mt-1 text-xs text-red-400">{{ emailError }}</p>
              </div>
              <input
                v-model="newName"
                type="text"
                :placeholder="t('newsletters.audience.name_placeholder')"
                class="flex-1 min-w-32 bg-slate-800 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-emerald-500"
                @keydown.enter.prevent="addManualEmail"
              />
              <button
                type="button"
                class="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white text-sm rounded-lg transition-colors"
                @click="addManualEmail"
              >
                {{ t('newsletters.audience.add_button') }}
              </button>
            </div>
          </div>

          <!-- Manual additions list -->
          <div v-if="manualAdditions.length > 0" class="rounded-lg border border-slate-700 overflow-hidden">
            <div class="px-4 py-2 bg-slate-800/50 border-b border-slate-700 text-xs text-slate-400 font-medium">
              {{ t('newsletters.audience.manually_added_header', { count: manualAdditions.length }) }}
            </div>
            <ul class="divide-y divide-slate-700/50">
              <li
                v-for="(addition, idx) in manualAdditions"
                :key="addition.email"
                class="flex items-center justify-between px-4 py-2.5 text-sm"
              >
                <div>
                  <span class="text-white">{{ addition.email }}</span>
                  <span v-if="addition.name" class="text-slate-400 ml-2">({{ addition.name }})</span>
                  <span class="ml-2 text-xs text-slate-500 italic">{{ t('newsletters.audience.added_manually_badge') }}</span>
                </div>
                <button
                  type="button"
                  class="text-slate-500 hover:text-red-400 transition-colors text-xs"
                  @click="removeManualAddition(idx)"
                >
                  {{ t('newsletters.audience.remove_addition') }}
                </button>
              </li>
            </ul>
          </div>
        </section>
      </div>
    </div>

    <!-- Footer -->
    <div class="shrink-0 px-6 py-4 border-t border-slate-700 flex items-center justify-between gap-4 bg-slate-900">
      <div>
        <p v-if="saveError" class="text-sm text-red-400">{{ saveError }}</p>
      </div>
      <div class="flex items-center gap-3">
        <button
          type="button"
          class="border border-slate-600 hover:border-slate-500 text-slate-300 hover:text-white font-semibold rounded-lg px-4 py-2 text-sm transition-colors"
          @click="emit('close')"
        >
          {{ t('newsletters.audience.cancel_button') }}
        </button>
        <button
          type="button"
          :disabled="saving || payload === null || statuses.length === 0"
          class="bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold rounded-lg px-5 py-2 text-sm transition-colors flex items-center gap-2"
          @click="handleSave"
        >
          <AppSpinner v-if="saving" size="button" />
          {{ t('newsletters.audience.use_recipients_button', { count: count?.total.toLocaleString() ?? '…' }) }}
        </button>
      </div>
    </div>
  </div>
</template>
